import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { EyeOff, Pencil, Plus, Trash2 } from "lucide-react";

import {
  AdminPage,
  Loading,
  SortableGrid,
  SortableGridItem,
  useRows,
} from "@/components/admin/AdminKit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { GripVertical } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/gala")({
  component: GalaAdmin,
});

type VideoForm = {
  id?: string;
  title: string;
  youtube_id: string;
  category: string;
  visible: boolean;
  sort_order: number;
};

function parseYouTubeId(value: string) {
  const v = value.trim();
  const match = v.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([A-Za-z0-9_-]{6,})/);
  return match ? match[1]! : v;
}

function GalaAdmin() {
  const { rows, isLoading, save, remove, reorder } = useRows("gala_videos");
  const [editing, setEditing] = useState<VideoForm | null>(null);

  if (isLoading) return <Loading />;

  return (
    <AdminPage
      title="Gala — materiale video"
      description="Trage cardurile pentru a le reordona. Editarea se face în fereastra dedicată."
      action={
        <Button
          onClick={() =>
            setEditing({
              title: "",
              youtube_id: "",
              category: "gala",
              visible: true,
              sort_order: rows.length,
            })
          }
        >
          <Plus className="mr-2 h-4 w-4" /> Adaugă video
        </Button>
      }
    >
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Niciun material video încă.</p>
      ) : (
        <SortableGrid items={rows} onReorder={reorder}>
          {(item) => (
            <SortableGridItem key={item.id} id={item.id}>
              {({ attributes, listeners }) => (
                <div className="group relative flex h-full flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm transition hover:shadow-md">
                  <button
                    type="button"
                    aria-label="Reordonează"
                    className="absolute left-2 top-2 z-10 cursor-grab rounded-md bg-background/85 p-1 text-muted-foreground backdrop-blur active:cursor-grabbing"
                    {...(attributes as object)}
                    {...(listeners as object)}
                  >
                    <GripVertical className="h-4 w-4" />
                  </button>
                  {!item["visible"] && (
                    <span className="absolute right-2 top-2 z-10 flex items-center gap-1 rounded-md bg-background/85 px-2 py-1 text-[11px] text-muted-foreground backdrop-blur">
                      <EyeOff className="h-3 w-3" /> ascuns
                    </span>
                  )}
                  <div className="aspect-video w-full overflow-hidden bg-muted">
                    {item["youtube_id"] ? (
                      <img
                        src={`https://img.youtube.com/vi/${String(item["youtube_id"])}/hqdefault.jpg`}
                        alt={String(item["title"] ?? "")}
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                        fără ID YouTube
                      </div>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col gap-1 p-3">
                    <p className="line-clamp-2 font-semibold">{String(item["title"] ?? "")}</p>
                    <p className="text-xs text-muted-foreground">
                      {String(item["youtube_id"] ?? "")}
                    </p>
                    <div className="mt-auto flex gap-2 pt-3">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() =>
                          setEditing({
                            id: item.id,
                            title: String(item["title"] ?? ""),
                            youtube_id: String(item["youtube_id"] ?? ""),
                            category: String(item["category"] ?? "gala"),
                            visible: Boolean(item["visible"]),
                            sort_order: Number(item["sort_order"] ?? 0),
                          })
                        }
                      >
                        <Pencil className="mr-2 h-3.5 w-3.5" /> Editează
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label="Șterge"
                        onClick={() => {
                          if (window.confirm(`Ștergi „${String(item["title"] ?? "")}”?`))
                            void remove(item.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </SortableGridItem>
          )}
        </SortableGrid>
      )}

      <Dialog open={editing !== null} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Editează materialul" : "Video nou"}</DialogTitle>
            <DialogDescription>
              Poți lipi linkul complet de YouTube — extragem automat ID-ul.
            </DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Titlu</Label>
                <Input
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Link sau ID YouTube</Label>
                <Input
                  value={editing.youtube_id}
                  onChange={(e) =>
                    setEditing({ ...editing, youtube_id: parseYouTubeId(e.target.value) })
                  }
                  placeholder="ex. dQw4w9WgXcQ"
                />
              </div>
              {editing.youtube_id && (
                <div className="overflow-hidden rounded-xl border border-border">
                  <img
                    src={`https://img.youtube.com/vi/${editing.youtube_id}/hqdefault.jpg`}
                    alt="Previzualizare"
                    className="aspect-video w-full object-cover"
                  />
                </div>
              )}
              <label className="flex items-center gap-2 text-sm">
                <Switch
                  checked={editing.visible}
                  onCheckedChange={(v) => setEditing({ ...editing, visible: v })}
                />
                Vizibil pe site
              </label>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Anulează
            </Button>
            <Button
              onClick={async () => {
                if (!editing) return;
                const ok = await save({ ...editing });
                if (ok) setEditing(null);
              }}
            >
              Salvează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPage>
  );
}
